import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  identifyUser,
  initAnalytics,
  resetAnalytics,
  trackPageView,
} from "@/lib/analytics";

/**
 * Initialises PostHog once, identifies the authenticated user, fires SPA
 * pageviews on every route change, and resets identity on logout.
 *
 * Mounted once near the top of the React tree (inside BrowserRouter and
 * AuthProvider).
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { user, role } = useAuth();
  const location = useLocation();

  // One-time init on mount.
  useEffect(() => {
    initAnalytics();
  }, []);

  // Identify on login, reset on logout.
  useEffect(() => {
    if (user) {
      identifyUser(user.id, {
        email: user.email,
        role: role ?? "unknown",
      });
    } else {
      resetAnalytics();
    }
  }, [user?.id, role]);

  // SPA pageviews.
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return <>{children}</>;
}
